import React, { useState } from 'react';
import goldColors from '../themes/goldColors';
import {
  AppBar,
  Toolbar,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  Typography,
  Button,
  InputBase,
  Breadcrumbs,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  alpha,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderActions } from './HeaderActions';
// 17 sep 2026: la campana era decorativa. Ahora muestra lo único que hoy
// exige una respuesta de Augusto y se enfría si nadie la ve: las
// solicitudes de consulta sin confirmar.
import useConsultasPendientes from '../hooks/useConsultasPendientes';
import useRevisionesPendientes from '../hooks/useRevisionesPendientes';
import TransactionForm from './TransactionForm';
import StatementImporter from './StatementImporter';

// Iconos
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonIcon from '@mui/icons-material/Person';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import SubjectIcon from '@mui/icons-material/Subject';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import SimpleCurrencySelector from './SimpleCurrencySelector';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

// Componentes estilizados
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.header,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: theme.zIndex.drawer + 1,
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
  display: 'flex',
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.common.black, 0.04),
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.06),
  },
}));

const SearchIconWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: 0,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
    fontSize: '0.875rem',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginRight: theme.spacing(2),
  flexGrow: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
}));

const NewButton = styled(Button)(({ theme }) => ({
  borderRadius: '4px',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: theme.spacing(0.5, 1.5),
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

// Componente principal
// 7 ago 2026 (Augusto: "al abrir en el celular se cortan accesos y
// features, no está apto para uso en browser en teléfono"): se agrega
// onMenuClick -- NotionLayout.js ya lo mandaba desde antes, pero este
// componente nunca lo recibía ni dibujaba un botón para usarlo, así que
// no había forma de abrir el menú en pantallas chicas.
const NotionHeader = ({ title, breadcrumbs, onMenuClick, sidebarOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const isSpanish = language === 'es';
  const { user, logout } = useAuth();
  const { pendientes: consultasPendientes } = useConsultasPendientes();
  // 21 sep 2026: la campana ahora suma dos cosas distintas -- consultas
  // por responder (solo admin) y movimientos importados por revisar
  // (cualquier usuario). Se priorizan las revisiones al hacer click
  // porque afectan los números del propio usuario; las consultas son
  // trabajo de Augusto, no del cliente.
  const { pendientes: revisionesPendientes } = useRevisionesPendientes();
  const totalAvisos = consultasPendientes + revisionesPendientes;

  // Estados para los menús
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElActions, setAnchorElActions] = useState(null);
  // 28 ago 2026 (ver app/services/budget_alerts.py en el backend): vive
  // acá y no en TransactionForm.js porque ese diálogo se cierra y la
  // pantalla navega justo después de crear la transacción -- el header
  // (este componente) es lo único que sigue montado para mostrarla.
  const [budgetAlert, setBudgetAlert] = useState(null);
  
  // Using the extracted header actions hook
  const {
    openNewTransactionDialog,
    openImportDialog,
    handleNewButtonClick,
    handleCloseNewTransactionDialog,
    handleImportButtonClick,
    handleCloseImportDialog,
    TransactionFormDialog,
    ImportDialog
  } = useHeaderActions();

  // Obtener iniciales del usuario para el avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    
    console.log('Datos de usuario para avatar en header:', user);
    
    // Usar displayName si existe, o combinar firstName y lastName
    const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`;
    
    // Si no hay ningún nombre, mostrar la primera letra del email o 'U'
    if (!displayName.trim()) {
      if (user.email) {
        return user.email.charAt(0).toUpperCase();
      }
      return 'U';
    }
    
    const initials = displayName.split(' ')
      .filter(part => part.length > 0) // Filtrar partes vacías
      .map(name => name.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
      
    console.log('Iniciales generadas en header:', initials);
    return initials;
  };

  // Handlers para menús
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenActionsMenu = (event) => {
    setAnchorElActions(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setAnchorElActions(null);
  };

  // These functions are now handled by the useHeaderActions hook

  const handleLogout = () => {
    handleCloseUserMenu();
    // Primero hacemos logout y luego navegamos
    logout();
    // La navegación ahora es responsabilidad de este componente
    navigate('/login');
  };

  const handleProfile = () => {
    handleCloseUserMenu();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleCloseUserMenu();
    navigate('/settings');
  };

  const handleDocumentation = () => {
    handleCloseActionsMenu();
    navigate('/docs');
  };

  // Generar migas de pan
  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return (
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" sx={{ color: goldColors.main }} />} 
        aria-label="breadcrumb"
        sx={{ display: { xs: 'none', md: 'flex' } }}
      >
        {breadcrumbs.map((crumb, index) => (
          <Typography 
            key={`breadcrumb-${index}`}
            variant="body2"
            color={index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary'}
            sx={{ 
              cursor: crumb.path ? 'pointer' : 'default',
              '&:hover': {
                color: crumb.path ? 'primary.main' : 'inherit',
              },
              fontWeight: index === breadcrumbs.length - 1 ? 500 : 400,
            }}
            onClick={() => crumb.path && navigate(crumb.path)}
          >
            {crumb.label}
          </Typography>
        ))}
      </Breadcrumbs>
    );
  };

  return (
    <StyledAppBar position="fixed">
      <Toolbar variant="dense">
        {/* Botón de menú -- en móvil abre/cierra el Drawer del sidebar; en
            escritorio (4 sep 2026, Augusto: "tiene que haber una opción
            para esconder la barra del lado izquierdo") esconde/muestra el
            sidebar fijo. Mismo botón, mismo handler (toggleDrawer en
            NotionLayout.js) -- ahí es donde cambia qué significa "open"
            según el tamaño de pantalla. Antes tenía
            `display: { xs: 'inline-flex', md: 'none' }`, o sea que en
            escritorio no existía ninguna forma de esconder el sidebar.
            4 sep 2026 (Augusto: "ese símbolo de tres rayas es muy poco
            intuitivo, parece una barra de menú, no parece nada referido
            a esconder el menu"): el ícono ahora cambia según el estado
            -- MenuOpenIcon (hamburguesa + flecha) cuando el sidebar está
            visible, para que se vea claramente que el clic lo cierra; y
            el hamburger normal cuando está escondido, para abrirlo. Es
            el mismo patrón que usa la propia documentación de MUI para
            drawers persistentes. */}
        {onMenuClick && (
          <Tooltip title={sidebarOpen ? (isSpanish ? 'Ocultar menú' : 'Hide menu') : (isSpanish ? 'Mostrar menú' : 'Show menu')}>
            <IconButton
              onClick={onMenuClick}
              size="small"
              sx={{ mr: 1, display: 'inline-flex' }}
              aria-label={sidebarOpen ? (isSpanish ? 'Ocultar menú' : 'Hide menu') : (isSpanish ? 'Mostrar menú' : 'Show menu')}
            >
              {sidebarOpen ? (
                <MenuOpenIcon sx={{ color: goldColors.main }} />
              ) : (
                <MenuIcon sx={{ color: goldColors.main }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Título o Migas de pan */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {title && !breadcrumbs && (
            <PageTitle variant="h6" component="h1">
              {title}
            </PageTitle>
          )}
          
          {renderBreadcrumbs()}
        </Box>
        
        {/* Búsqueda y acciones */}
        {/* 7 ago 2026: oculta en pantallas chicas -- con "New" + Importar +
            más acciones + notificaciones + avatar, la barra de búsqueda a
            ancho completo (100% en xs, ver SearchBox arriba) desbordaba el
            resto del header en el celular ("contenido se ve cortado"). */}
        <SearchBox sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <SearchIconWrapper>
            <SearchIcon fontSize="small" sx={{ color: goldColors.main }} />
          </SearchIconWrapper>
          {/* 30 oct 2026 (Augusto: "el search no funciona!!!!!"): era
              decorativo. Enter lleva a Transacciones filtrado por el texto
              (ver textoBusqueda en TransactionsPage.js). */}
          <StyledInputBase
            placeholder={language === 'es' ? 'Buscar movimientos…' : 'Search transactions…'}
            inputProps={{ 'aria-label': 'search' }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const q = e.currentTarget.value.trim();
              navigate(q ? `/transactions?q=${encodeURIComponent(q)}` : '/transactions');
            }}
          />
        </SearchBox>
        
        <ActionButtons>
          {/* Nuevo */}
          <NewButton 
            variant="contained" 
            startIcon={<AddIcon sx={{ color: '#FFFFFF' }} />}
            onClick={handleNewButtonClick}
            sx={{ mr: 1 }}
          >
            {t('common.new', 'New')}
          </NewButton>
          
          {/* Botón de importación -- oculto en pantallas chicas (7 ago
              2026, mismo motivo que la barra de búsqueda arriba). Sigue
              disponible desde escritorio; no es una acción que se use en
              el momento desde el celular como sí "Nuevo". */}
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={handleImportButtonClick}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              borderRadius: '8px',
              borderColor: alpha(goldColors.main, 0.5),
              color: goldColors.main,
              '&:hover': {
                borderColor: goldColors.main,
                backgroundColor: alpha(goldColors.main, 0.04)
              }
            }}
          >
            {t('common.import', 'Import')}
          </Button>
          
          {/* Dialog para añadir nueva transacción */}
          <Dialog 
            open={openNewTransactionDialog} 
            onClose={handleCloseNewTransactionDialog}
            fullWidth
            maxWidth="md"
          >
            {/* 21 sep 2026 (Augusto: "no me deja arrastrar el formulario
                de una nueva transacción"). Este diálogo no tenía barra de
                título -- solo contenido -- así que no había de dónde
                agarrarlo: el arrastre se toma del título (ver
                utils/arrastrarDialogos.js). De paso gana un encabezado y una
                X, que es lo que uno espera de una ventana. */}
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
              {t('transactions.new', 'Nueva transacción')}
              <IconButton size="small" onClick={handleCloseNewTransactionDialog} aria-label="cerrar">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {openNewTransactionDialog && (
                <TransactionForm onClose={handleCloseNewTransactionDialog} onBudgetAlert={setBudgetAlert} />
              )}
            </DialogContent>
          </Dialog>

          {/* 28 ago 2026: alerta de presupuesto tras registrar un gasto
              manual (ver app/services/budget_alerts.py) -- mismo mensaje
              que ya se manda por WhatsApp/voz, acá como Snackbar porque
              la app no tiene notificaciones push propias. */}
          <Snackbar
            open={!!budgetAlert}
            autoHideDuration={8000}
            onClose={() => setBudgetAlert(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="warning" onClose={() => setBudgetAlert(null)} sx={{ whiteSpace: 'pre-line' }}>
              {budgetAlert}
            </Alert>
          </Snackbar>

          {/* Diálogo para importar estados de cuenta.
              3 ago 2026 (pedido de Augusto): maxWidth subido de "md" a "lg"
              -- la tabla de transacciones detectadas (Fecha, Descripción,
              Monto, Categoría, Proveedor, Acciones) necesita ~850px de
              ancho mínimo y con "md" (900px, menos el padding del
              DialogContent) no entraba, obligando a hacer scroll lateral
              para revisar las transacciones antes de importar. */}
          <Dialog
            open={openImportDialog}
            onClose={(event, reason) => {
              // 3 ago 2026 (pedido de Augusto): antes cualquier clic fuera
              // de esta ventana (el fondo oscuro) la cerraba sin avisar --
              // ahora que el procesamiento puede tardar varios minutos
              // (ver StatementImporter.js), un clic accidental afuera
              // perdía todo el progreso. Ahora solo se cierra con la X de
              // adentro o con Escape, nunca por backdropClick.
              if (reason === 'backdropClick') return;
              handleCloseImportDialog();
            }}
            fullWidth
            maxWidth="lg"
          >
            <DialogContent>
              {openImportDialog && (
                <StatementImporter onClose={handleCloseImportDialog} />
              )}
            </DialogContent>
          </Dialog>

          {/* Más acciones */}
          <Tooltip title={t('common.moreActions', 'More actions')}>
            <IconButton onClick={handleOpenActionsMenu} size="small">
              <MoreHorizIcon sx={{ color: goldColors.main }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorElActions}
            open={Boolean(anchorElActions)}
            onClose={handleCloseActionsMenu}
            PaperProps={{
              elevation: 1,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  borderRadius: '4px',
                  mx: 0.5,
                  fontSize: '0.875rem',
                  minWidth: 150,
                },
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleCloseActionsMenu}>
              <ListItemIcon>
                <ThemeSwitcher />
              </ListItemIcon>
              {t('settings.theme', 'Theme')}
            </MenuItem>
            <MenuItem onClick={handleCloseActionsMenu}>
              <ListItemIcon>
                <LanguageSwitcher iconOnly={true} />
              </ListItemIcon>
              {t('settings.language', 'Language')}
            </MenuItem>
            <MenuItem>
              <ListItemIcon>
                <CurrencyExchangeIcon sx={{ color: goldColors.main }} />
              </ListItemIcon>
              <Box sx={{ width: '100%' }}>
                <SimpleCurrencySelector fullWidth menuPlacement="right" />
              </Box>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleDocumentation}>
              <ListItemIcon>
                <SubjectIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              {t('docs.title', 'Documentation')}
            </MenuItem>
          </Menu>
          
          {/* Notificaciones. Hasta el 17 sep 2026 este botón no hacía nada.
              Hoy avisa de las solicitudes de consulta pendientes y lleva
              directo al panel -- antes había que acordarse de bajar hasta
              el final de la barra lateral. Para un usuario que no es
              admin, `pendientes` siempre es 0 y la campana se comporta
              como antes. */}
          <Tooltip
            title={(() => {
              if (totalAvisos === 0) return t('notifications.title', 'Notifications');
              const partes = [];
              if (revisionesPendientes > 0) {
                partes.push(
                  language === 'es'
                    ? `${revisionesPendientes} movimiento(s) por revisar (no están sumando en tus totales)`
                    : `${revisionesPendientes} transaction(s) to review (not counting toward your totals)`
                );
              }
              if (consultasPendientes > 0) {
                partes.push(
                  language === 'es'
                    ? `${consultasPendientes} solicitud(es) de consulta sin responder`
                    : `${consultasPendientes} consultation request(s) awaiting reply`
                );
              }
              return partes.join(' · ');
            })()}
          >
            <IconButton
              size="small"
              onClick={
                revisionesPendientes > 0
                  ? () => navigate('/revision')
                  : consultasPendientes > 0
                    ? () => navigate('/consultas')
                    : undefined
              }
            >
              <Badge badgeContent={totalAvisos} color="error">
                <NotificationsNoneIcon sx={{ color: goldColors.main }} />
              </Badge>
            </IconButton>
          </Tooltip>
          
          {/* Menú de Usuario */}
          <Tooltip title={t('profile.title', 'Profile')}>
            <IconButton onClick={handleOpenUserMenu} size="small">
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  fontSize: '0.875rem', 
                  bgcolor: goldColors.main,
                  color: '#fff'
                }}
              >
                {getUserInitials()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{
              elevation: 1,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  borderRadius: '4px',
                  mx: 0.5,
                  fontSize: '0.875rem',
                  minWidth: 150,
                },
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {user && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  {user.email}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <PersonOutlineIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              {t('profile.title', 'Profile')}
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsOutlinedIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              {t('settings.title', 'Settings')}
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" sx={{ color: goldColors.main }} />
              </ListItemIcon>
              {t('auth.logout', 'Logout')}
            </MenuItem>
          </Menu>
        </ActionButtons>
      </Toolbar>
    </StyledAppBar>
  );
};

export default NotionHeader;
